import styled from 'styled-components'
import { ButtonBase, styled as muiStyled } from '@mui/material'

// Consistent color scheme for inner pages
export const TILE_COLORS = {
  // Patient Management colors
  therapyActions: 'linear-gradient(135deg, rgba(99, 102, 241, 0.95) 0%, rgba(67, 56, 202, 0.95) 100%)',
  patientList: 'linear-gradient(135deg, rgba(139, 92, 246, 0.95) 0%, rgba(109, 40, 217, 0.95) 100%)',
  therapyScheduled: 'linear-gradient(135deg, rgba(59, 130, 246, 0.95) 0%, rgba(37, 99, 235, 0.95) 100%)',
  consultationScheduled: 'linear-gradient(135deg, rgba(168, 85, 247, 0.95) 0%, rgba(147, 51, 234, 0.95) 100%)',
  
  // Practitioner Management colors
  therapists: 'linear-gradient(135deg, rgba(216, 101, 0, 0.95) 0%, rgba(194, 65, 12, 0.95) 100%)',
  doctors: 'linear-gradient(135deg, rgba(0, 43, 107, 0.95) 0%, rgba(30, 58, 138, 0.95) 100%)',
  practitionerOnboarding: 'linear-gradient(135deg, rgba(7, 89, 133, 0.95) 0%, rgba(15, 118, 110, 0.95) 100%)',
  payoutReceipts: 'linear-gradient(135deg, rgba(20, 152, 18, 0.95) 0%, rgba(22, 163, 74, 0.95) 100%)',
  serviceApprovals: 'linear-gradient(135deg, rgba(34, 193, 195, 0.95) 0%, rgba(14, 165, 233, 0.95) 100%)',
  applicants: 'linear-gradient(135deg, rgba(244, 63, 94, 0.95) 0%, rgba(225, 29, 72, 0.95) 100%)',
  screeners: 'linear-gradient(135deg, rgba(217, 70, 239, 0.95) 0%, rgba(168, 85, 247, 0.95) 100%)',
  invitations: 'linear-gradient(135deg, rgba(245, 158, 11, 0.95) 0%, rgba(217, 119, 6, 0.95) 100%)',
  // Phase 14i — kyc-approved-but-disabled practitioners. Slate-grey palette to read as
  // "paused / awaiting action" without screaming alarm-red.
  disabledPractitioners: 'linear-gradient(135deg, rgba(100, 116, 139, 0.95) 0%, rgba(71, 85, 105, 0.95) 100%)',
  // Practitioner enable/disable access log — deep indigo, reads as "audit / history".
  accessLogs: 'linear-gradient(135deg, rgba(79, 70, 229, 0.95) 0%, rgba(55, 48, 163, 0.95) 100%)',

  // Clinics & Hospitals colors
  clinics: 'linear-gradient(135deg, rgba(17, 39, 71, 0.95) 0%, rgba(30, 58, 138, 0.95) 100%)',
  clinicRoster: 'linear-gradient(135deg, rgba(198, 82, 67, 0.95) 0%, rgba(185, 28, 28, 0.95) 100%)',
  clinicBrandInvoices: 'linear-gradient(135deg, rgba(0, 152, 186, 0.95) 0%, rgba(14, 116, 144, 0.95) 100%)',
  
  // Important Actions colors
  interventionsRequested: 'linear-gradient(135deg, rgba(16, 185, 129, 0.95) 0%, rgba(5, 150, 105, 0.95) 100%)',
  bookingsAppointments: 'linear-gradient(135deg, rgba(0, 152, 186, 0.95) 0%, rgba(14, 116, 144, 0.95) 100%)',
  paymentLinks: 'linear-gradient(135deg, rgba(37, 99, 235, 0.95) 0%, rgba(79, 70, 229, 0.95) 100%)',
  docSide: 'linear-gradient(135deg, rgba(34, 193, 195, 0.95) 0%, rgba(253, 187, 45, 0.95) 100%)',
  overallRoster: 'linear-gradient(135deg, rgba(34, 139, 34, 0.95) 0%, rgba(0, 128, 0, 0.95) 100%)',
  customCommunications: 'linear-gradient(135deg, rgba(124, 58, 237, 0.95) 0%, rgba(30, 64, 175, 0.95) 100%)',
}

export const SupportHomePageContainer = muiStyled('div')(
  ({ theme }) => `
  width: 100%;
  padding: 24px 20px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 18px;
  position: relative;
  background: linear-gradient(180deg, rgba(249, 250, 251, 0.5) 0%, rgba(243, 244, 246, 0.3) 100%);
  align-content: start;

  @media (min-width: ${theme.breakpoints.values.sm}px) {
    padding: 28px 24px;
    gap: 20px;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  }

  @media (min-width: ${theme.breakpoints.values.md}px) {
    padding: 32px 28px;
    gap: 22px;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  }

  @media (min-width: ${theme.breakpoints.values.lg}px) {
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  }
`,
)

// Helper function to extract and brighten color from gradient
const getBorderColor = (gradient) => {
  if (!gradient) return 'rgba(167, 139, 250, 0.8)'
  
  // Extract first color from gradient (most common pattern: rgb(...) or rgba(...))
  const colorMatch = gradient.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/)
  if (colorMatch) {
    const r = Math.min(255, parseInt(colorMatch[1]) + 40)
    const g = Math.min(255, parseInt(colorMatch[2]) + 40)
    const b = Math.min(255, parseInt(colorMatch[3]) + 40)
    return `rgba(${r}, ${g}, ${b}, 0.9)`
  }
  
  // Fallback: try to extract hex or named colors
  if (gradient.includes('#9747FF')) return 'rgba(167, 139, 250, 0.9)'
  if (gradient.includes('rgb(216, 101, 0)')) return 'rgba(255, 141, 40, 0.9)'
  if (gradient.includes('rgb(0, 43, 107)')) return 'rgba(40, 83, 147, 0.9)'
  if (gradient.includes('rgb(17, 39, 71)')) return 'rgba(57, 79, 111, 0.9)'
  if (gradient.includes('rgb(0, 152, 186)')) return 'rgba(40, 192, 226, 0.9)'
  if (gradient.includes('rgb(223, 179, 2)')) return 'rgba(255, 219, 42, 0.9)'
  if (gradient.includes('rgb(22, 211, 214)')) return 'rgba(62, 251, 254, 0.9)'
  if (gradient.includes('rgb(20, 152, 18)')) return 'rgba(60, 192, 58, 0.9)'
  
  return 'rgba(167, 139, 250, 0.9)'
}

export const SupportHomePageItem = muiStyled(ButtonBase, {
  shouldForwardProp: (prop) => prop !== 'otherColor',
})(
  ({ theme, otherColor }) => {
    const borderColor = getBorderColor(otherColor)
    const defaultGradient = 'linear-gradient(135deg, rgba(139, 92, 246, 0.95) 0%, rgba(124, 58, 237, 0.95) 50%, rgba(109, 40, 217, 0.95) 100%)'
    const background = otherColor || defaultGradient
    
    return `
    position: relative;
    width: 100%;
    aspect-ratio: 2.2 / 1;
    max-height: 80px;
    flex-shrink: 0;
    border-radius: 12px;
    background: ${background};
    backdrop-filter: blur(8px) saturate(180%);
    -webkit-backdrop-filter: blur(8px) saturate(180%);
    border: none;
    box-shadow: 
      0 2px 8px rgba(0, 0, 0, 0.12),
      0 1px 3px rgba(0, 0, 0, 0.08);
    cursor: pointer;
    color: #FFFFFF;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    padding: 12px 16px;
    gap: 14px;
    overflow: hidden;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);

    /* Glass overlay effect */
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%);
      pointer-events: none;
      border-radius: 12px;
    }

    &:hover {
      transform: translateY(-2px);
      box-shadow: 
        0 4px 16px rgba(0, 0, 0, 0.18),
        0 2px 6px rgba(0, 0, 0, 0.12);
    }

    &:active {
      transform: translateY(-1px);
      transition: all 0.1s;
    }

    @media (min-width: ${theme.breakpoints.values.sm}px) {
      aspect-ratio: 2.4 / 1;
      max-height: 85px;
      padding: 14px 18px;
      gap: 16px;
      border-radius: 14px;
    }

    @media (min-width: ${theme.breakpoints.values.md}px) {
      aspect-ratio: 2.6 / 1;
      max-height: 90px;
      padding: 16px 20px;
      gap: 18px;
      border-radius: 16px;
    }
  `
  },
)

export const TileIconContainer = muiStyled('div')(
  ({ theme }) => `
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    min-width: 44px;
    min-height: 44px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.25);
    backdrop-filter: blur(10px) saturate(180%);
    -webkit-backdrop-filter: blur(10px) saturate(180%);
    box-shadow: 
      0 2px 8px rgba(0, 0, 0, 0.15),
      0 1px 3px rgba(0, 0, 0, 0.1),
      inset 0 1px 0 rgba(255, 255, 255, 0.3);
    
    & svg {
      font-size: 22px;
      color: #FFFFFF;
      filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
    }

    @media (min-width: ${theme.breakpoints.values.sm}px) {
      width: 48px;
      height: 48px;
      min-width: 48px;
      min-height: 48px;
      border-radius: 11px;
      
      & svg {
        font-size: 24px;
      }
    }

    @media (min-width: ${theme.breakpoints.values.md}px) {
      width: 52px;
      height: 52px;
      min-width: 52px;
      min-height: 52px;
      border-radius: 12px;
      
      & svg {
        font-size: 26px;
      }
    }
  `,
)

export const TileText = muiStyled('span')(
  ({ theme }) => `
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    flex: 1;
    font-size: 14px;
    font-weight: 700;
    line-height: 1.3;
    letter-spacing: -0.01em;
    color: #FFFFFF;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.25), 0 1px 1px rgba(0, 0, 0, 0.2);
    word-break: break-word;

    @media (min-width: ${theme.breakpoints.values.sm}px) {
      font-size: 15px;
    }

    @media (min-width: ${theme.breakpoints.values.md}px) {
      font-size: 16px;
    }
  `,
)
